/**
 * ClassificationClient
 * Handles frame uploads to backend and classification result retrieval
 * 
 * Requirements: 3.4, 3.5, 9.4, 14.1, 14.2
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';
import type { ClassificationResult, Criterion } from '../types';

export type ClassificationResultCallback = (result: ClassificationResult) => void;
export type ErrorCallback = (error: Error) => void;
export type NetworkStatusCallback = (online: boolean) => void;

export interface UploadOptions {
  maxRetries?: number;
  retryDelayMs?: number;
  timeoutMs?: number;
}

export interface NetworkMetrics {
  averageLatencyMs: number;
  successRate: number;
  totalRequests: number;
  failedRequests: number;
}

export class ClassificationClient {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;
  private resultCallback: ClassificationResultCallback | null = null;
  private errorCallback: ErrorCallback | null = null;
  private networkStatusCallback: NetworkStatusCallback | null = null;
  
  // Network state
  private isOnline: boolean = navigator.onLine;
  private isPaused: boolean = false;
  
  // Timeout configuration
  private timeoutMs: number = 30000; // 30 second timeout (increased for Gemini API)
  
  // Network metrics
  private metrics: NetworkMetrics = {
    averageLatencyMs: 0,
    successRate: 1.0,
    totalRequests: 0,
    failedRequests: 0,
  };
  private latencyHistory: number[] = [];
  private maxLatencyHistory: number = 10;

  constructor(options?: UploadOptions) {
    this.authService = AuthService.getInstance();
    
    // Apply custom timeout option
    if (options?.timeoutMs !== undefined) {
      this.timeoutMs = options.timeoutMs;
    }

    // Create axios instance with base configuration
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: this.timeoutMs,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Add request interceptor to include JWT token
    // Requirement 9.4
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Failed to get access token:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Unauthorized - token invalid
          console.error('Authentication failed, logging out');
          await this.authService.logout();
          // Notify error callback
          if (this.errorCallback) {
            this.errorCallback(new Error('Authentication failed. Please log in again.'));
          }
        }
        return Promise.reject(error);
      }
    );

    // Setup network status monitoring
    // Requirements 14.1, 14.2
    this.setupNetworkMonitoring();
  }

  /**
   * Upload frame to backend for classification
   * Requirements: 3.4, 3.5, 9.4
   */
  async uploadFrame(frameBlob: Blob, frameId: string): Promise<ClassificationResult> {
    // For backward compatibility, wrap single image in array
    const results = await this.uploadFrames([{ blob: frameBlob, frameId }]);
    return results[0];
  }

  /**
   * Upload multiple frames to backend for classification in a single request
   * Requirements: 3.4, 3.5, 9.4
   * 
   * Credit consumption:
   * - 1 image: 1 credit
   * - 2-3 images: 2 credits (batch discount)
   */
  async uploadFrames(frames: Array<{ blob: Blob; frameId: string }>): Promise<ClassificationResult[]> {
    // Check if online
    // Requirement 14.1
    if (!this.isOnline || this.isPaused) {
      throw new Error('Network offline or uploads paused');
    }

    // Validate frame count
    if (frames.length < 1 || frames.length > 3) {
      throw new Error('Invalid number of images. Expected 1-3 images.');
    }

    // Validate frames
    for (const frame of frames) {
      if (!frame.blob || frame.blob.size === 0) {
        throw new Error('Invalid image data');
      }
      if (frame.blob.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('Image too large. Maximum size is 10MB.');
      }
    }

    const startTime = Date.now();
    let formData: FormData | null = null;

    // NO AUTOMATIC RETRIES - prevents duplicate credit charges
    // User must manually retry by clicking submit again if request fails
    try {
      // Create form data with multiple images
      formData = new FormData();
      frames.forEach((frame) => {
        formData!.append('images', frame.blob, `${frame.frameId}.jpg`);
      });

      console.log(`[ClassificationClient] Uploading ${frames.length} images...`);

      // Upload to backend (single attempt only)
      const response = await this.axiosInstance.post<ClassificationResult[]>(
        '/api/classify',
        formData,
        {
          // Add progress tracking for large uploads
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`[ClassificationClient] Upload progress: ${percentCompleted}%`);
            }
          },
          // Increase timeout for mobile networks
          timeout: this.timeoutMs,
          // Ensure proper headers
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Debug logging to see what we received
      console.log('[ClassificationClient] Received response from backend:', {
        status: response.status,
        dataLength: response.data?.length,
        data: response.data
      });

      // Validate response
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error('Invalid response format from server');
      }

      if (response.data.length === 0) {
        throw new Error('No classification results received');
      }

      // Track metrics
      const latency = Date.now() - startTime;
      this.updateMetrics(latency, true);

      // Emit results to callback
      if (this.resultCallback) {
        response.data.forEach(result => this.resultCallback!(result));
      }

      console.log(`[ClassificationClient] Successfully processed ${response.data.length} results in ${latency}ms`);
      return response.data;
    } catch (error) {
      // Track failed request
      const latency = Date.now() - startTime;
      this.updateMetrics(latency, false);

      console.error('[ClassificationClient] Upload failed:', error);

      // Handle and throw error
      const handledError = this.handleUploadError(error, 0);
      
      // Emit error to callback
      if (this.errorCallback) {
        this.errorCallback(handledError);
      }

      throw handledError;
    } finally {
      // Clean up FormData to free memory
      formData = null;
    }
  }

  /**
   * Get active classification criteria from backend
   * Requirement 8.5
   */
  async getCriteria(): Promise<Criterion[]> {
    try {
      const response = await this.axiosInstance.get<{ criteria: Criterion[] }>(
        '/api/criteria'
      );
      return response.data.criteria;
    } catch (error) {
      console.error('Failed to fetch criteria:', error);
      throw this.handleUploadError(error, 0);
    }
  }

  /**
   * Register callback for classification results
   */
  onResult(callback: ClassificationResultCallback): void {
    this.resultCallback = callback;
  }

  /**
   * Register callback for errors
   */
  onError(callback: ErrorCallback): void {
    this.errorCallback = callback;
  }

  /**
   * Register callback for network status changes
   */
  onNetworkStatusChange(callback: NetworkStatusCallback): void {
    this.networkStatusCallback = callback;
  }

  /**
   * Check if client is online
   */
  isClientOnline(): boolean {
    return this.isOnline;
  }

  /**
   * Check if uploads are paused
   */
  isUploadsPaused(): boolean {
    return this.isPaused;
  }

  /**
   * Pause uploads (for network issues or user action)
   * Requirement 14.1
   */
  pauseUploads(): void {
    this.isPaused = true;
    console.log('Uploads paused');
  }

  /**
   * Resume uploads
   * Requirement 14.2
   */
  resumeUploads(): void {
    this.isPaused = false;
    console.log('Uploads resumed');
  }

  /**
   * Get current network metrics
   */
  getNetworkMetrics(): NetworkMetrics {
    return { ...this.metrics };
  }

  /**
   * Get average upload latency
   */
  getAverageLatency(): number {
    return this.metrics.averageLatencyMs;
  }

  /**
   * Check if network is slow (latency > 5 seconds)
   * Requirement 14.3
   */
  isNetworkSlow(): boolean {
    return this.metrics.averageLatencyMs > 5000;
  }

  /**
   * Setup network status monitoring
   * Requirements 14.1, 14.2
   */
  private setupNetworkMonitoring(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      console.log('Network online');
      this.isOnline = true;
      this.resumeUploads();
      
      if (this.networkStatusCallback) {
        this.networkStatusCallback(true);
      }
    });

    window.addEventListener('offline', () => {
      console.log('Network offline');
      this.isOnline = false;
      this.pauseUploads();
      
      if (this.networkStatusCallback) {
        this.networkStatusCallback(false);
      }
    });
  }

  /**
   * Handle upload errors and convert to user-friendly messages
   * Requirements: 9.1, 9.2, 9.4, 9.5
   */
  private handleUploadError(error: unknown, _attempt: number): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      // Requirement 9.1: Handle network errors
      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timed out. Please check your connection and try again.');
      }

      if (axiosError.code === 'ERR_NETWORK') {
        return new Error('Network error. Please check your connection and try again.');
      }

      if (axiosError.response) {
        const status = axiosError.response.status;
        const data = axiosError.response.data as any;
        
        // Requirement 9.5: Parse backend error responses
        // Backend returns: {"error": {"code": "...", "message": "...", "details": {...}}}
        let backendMessage = data?.error?.message || data?.detail || data?.message;
        
        // If backendMessage is still an object, try to extract the actual message
        if (backendMessage && typeof backendMessage === 'object') {
          backendMessage = backendMessage.message || backendMessage.detail || JSON.stringify(backendMessage);
        }
        
        // Ensure it's a string
        if (backendMessage && typeof backendMessage !== 'string') {
          backendMessage = String(backendMessage);
        }

        switch (status) {
          case 400:
            // Requirement 9.4: Display validation error details
            if (backendMessage) {
              return new Error(`${backendMessage} Please try again.`);
            }
            return new Error('Invalid image format or size. Please try again.');
          
          case 401:
            return new Error('Authentication failed. Please log in again.');
          
          case 402:
          case 403:
            // Requirement 9.2: Handle insufficient credits error specifically
            if (backendMessage && backendMessage.toLowerCase().includes('credit')) {
              return new Error(`${backendMessage}`);
            }
            if (backendMessage) {
              return new Error(backendMessage);
            }
            return new Error('You do not have permission to perform this action.');
          
          case 413:
            return new Error('Image too large. Maximum size is 5MB. Please try again.');
          
          case 422:
            // Unprocessable entity - validation errors
            if (backendMessage) {
              return new Error(`${backendMessage} Please try again.`);
            }
            return new Error('Invalid request data. Please check your input and try again.');
          
          case 429:
            return new Error('Too many requests. Please wait a moment before trying again.');
          
          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors - user should retry
            if (backendMessage) {
              return new Error(`${backendMessage} Please try again.`);
            }
            return new Error('Server error. Please try again.');
          
          default:
            // Requirement 9.5: Use backend message if available
            if (backendMessage) {
              return new Error(`${backendMessage} Please try again.`);
            }
            return new Error(`Request failed. Please try again.`);
        }
      }

      return new Error('Network error. Please check your connection.');
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error('Unknown error occurred during upload');
  }

  /**
   * Update network metrics
   */
  private updateMetrics(latencyMs: number, success: boolean): void {
    this.metrics.totalRequests++;
    
    if (!success) {
      this.metrics.failedRequests++;
    }

    // Update success rate
    this.metrics.successRate = 
      (this.metrics.totalRequests - this.metrics.failedRequests) / 
      this.metrics.totalRequests;

    // Update latency history
    this.latencyHistory.push(latencyMs);
    if (this.latencyHistory.length > this.maxLatencyHistory) {
      this.latencyHistory.shift();
    }

    // Calculate average latency
    this.metrics.averageLatencyMs = 
      this.latencyHistory.reduce((sum, val) => sum + val, 0) / 
      this.latencyHistory.length;
  }



  /**
   * Reset metrics
   */
  resetMetrics(): void {
    this.metrics = {
      averageLatencyMs: 0,
      successRate: 1.0,
      totalRequests: 0,
      failedRequests: 0,
    };
    this.latencyHistory = [];
  }

  /**
   * Update configuration
   */
  updateConfig(options: UploadOptions): void {
    if (options.timeoutMs !== undefined) {
      this.timeoutMs = options.timeoutMs;
      this.axiosInstance.defaults.timeout = this.timeoutMs;
    }
  }
}

export default ClassificationClient;
